import React, { useCallback } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useTranslation, Trans } from 'react-i18next';
import { RouteComponentProps } from 'react-router-dom';

import GridShowMoreButton from '@common/components/grid-show-more';

import { useThunkDispatch } from '../../../../common/hooks';
import { pageWrapper, EEventNames } from '../../../../../client/analytics';
import { SubNavigationBar, TopNavigationBar } from '@common/components/navigation-bar';
import { EventsPlaceholder } from '../../components/events-placeholder';
import Container from '../../../../common/components/container';
import { Button } from '../../../../common/components/button';
import AsyncList from '../../../../common/components/list/async';
import Permission from '../../../../common/components/permission';
import { EventItemAdminComponent } from '../../components/event-item';
import * as organisationSelector from '../../../organisation/selectors/organisation';
import * as userSelector from '../../../core/selectors/logged-user';
import { EPermissions } from '../../../../common/definitions';
import * as eventActions from '../../actions';
import * as eventsSelector from '../../selector';
import { EEventStatus } from '../../definitions';
import type { EventForAdminDto } from '../../types';
import type { StoreState } from '../../../../common/types/store';
import { Api } from '@common/services/api';
import { AlertService } from '@common/services/alert';
import { Organisation } from '@common/types/objects';
import { useEncodedFullPath } from '@common/hooks/url';

interface Props extends RouteComponentProps<{ networkId?: string, filter?: 'past' | 'draft' | 'canceled' }> {
  counts: {
    upcoming: number;
    past: number;
    draft: number;
    canceled: number;
  };
  events: EventForAdminDto[];
  organisation: Organisation;
}

const mapStateToProps = (state: StoreState) => ({
  loggedUser: userSelector.selected(state),
  organisation: organisationSelector.selected(state),
  events: eventsSelector.getAdminScopeEvents(state),
  counts: state.events.counts,
});

const connector = connect(mapStateToProps);

export const UnwrappedAdminEventsContainer = ({
  history,
  match,
  events,
  counts,
  organisation
}: Props) => {
  const { t } = useTranslation();
  const thunkDispatch = useThunkDispatch();

  const statusLookup = {
    all: {
      filter: undefined,
      count: counts.upcoming,
    },
    past: {
      filter: EEventStatus.PAST,
      count: counts.past,
    },
    draft: {
      filter: EEventStatus.DRAFT,
      count: counts.draft,
    },
    canceled: {
      filter: EEventStatus.CANCELED,
      count: counts.canceled,
    },
  };

  // Map url to status
  const currentStatus = statusLookup[match.params.filter || 'all'];

  const orgId = organisation.id;
  const dispatch = useDispatch();
  const onDuplicate = useCallback(async (item: EventForAdminDto) => {
    try {
      const url = `/v1/organisations/${orgId}/events/${item.id}/duplicate`;
      const response = await Api.post(url);
      dispatch({
        type: 'events/DUPLICATE_EVENT',
        response,
        originalEventId: item.id
      });
      history.push('/admin/events/filter/draft');
    } catch (error: any) {
      AlertService.error(t('events:duplicate_error'));
      throw error;
    }
  }, [orgId, t, history, dispatch]);

  const encodedPath = useEncodedFullPath();

  return (
    <>
      <SubNavigationBar title={t('core:tab_events')}>
        <SubNavigationBar.Item
          exact
          icon="date_start"
          path="/admin/events"
          title={t('events:filter_upcoming')}
          count={counts.upcoming}
        />
        <SubNavigationBar.Item
          icon="draft"
          path="/admin/events/filter/draft"
          title={t('events:filter_draft')}
          count={counts.draft}
        />
        <SubNavigationBar.Item
          icon="event_busy"
          path="/admin/events/filter/canceled"
          title={t('events:filter_canceled')}
          count={counts.canceled}
        />
        <SubNavigationBar.Item
          icon="inventory"
          path="/admin/events/filter/past"
          title={t('events:filter_past')}
          count={counts.past}
        />
      </SubNavigationBar>
      <Container name="Events">
        <TopNavigationBar
          title={t('events:filter', { context: match.params.filter || 'upcoming' })}
          count={currentStatus.count}
          action={(
            <Permission name={EPermissions.ORGANISATION_EVENTS_CREATE}>
              <Button type="primary" iconRight="add" onClick={() => history.push('/admin/events/create')}>
                <Trans i18nKey="events:create_event" />
              </Button>
            </Permission>
          )}
        />
        <Container.Content>
          <AsyncList
            containerClassName="EventsList"
            // @ts-expect-error
            items={events}
            data={{
              useCursor: true,
              dispatch: thunkDispatch,
              // @ts-expect-error
              onFetch: eventActions.fetchEvents,
              filter: {
                // @ts-expect-error
                filter: currentStatus.filter || 'UPCOMING',
              },
            }}
            renderRow={EventItemAdminComponent}
            // @ts-expect-error
            rowProps={(item: EventForAdminDto) => ({
              networkId: match.params.networkId,
              onClick: () => history.push(`/admin/events/${item.id}?back=${encodedPath}`),
              onEdit: () => history.push(`/admin/events/edit/${item.id}?back=${encodedPath}`),
              onDuplicate: () => onDuplicate(item)
            })}
            placeholder={<EventsPlaceholder />}
            ShowMoreComponent={({ onShowMore, isFetching }) => (
              <GridShowMoreButton onShowMore={onShowMore} isFetching={isFetching} />
            )}
          />
        </Container.Content>
      </Container>
    </>
  );
};

export const AdminEventsContainer = pageWrapper(EEventNames.VISITED_SURVEYS_PAGE)(connector(UnwrappedAdminEventsContainer));
